import { ReactNode } from "react";
import { Dialog, IconButton, Grow, type SxProps, type Theme } from "@mui/material";
import { useTranslation } from "next-i18next";
import { IconClose } from "src/components/icons-mui/close";
import { blurActiveElement } from "src/utils/dom/focus/focus";

/**
 * Themed Dialog component following wikifolio UI Library design specifications
 *
 * https://www.figma.com/design/zzvO7fdaTfLvgTSrb6ypCs/wikifolio-UI-Library?node-id=3431-2441&m=dev
 *
 */

export const WF_DIALOG_SPACE_VARIANTS = {
    DEFAULT: "default",
    MOBILE_SPACIOUS_TOP: "mobile-spacious-top",
    NONE: "none",
} as const;

export type WfDialogSpaceVariant = (typeof WF_DIALOG_SPACE_VARIANTS)[keyof typeof WF_DIALOG_SPACE_VARIANTS];

const getSpacing = (space: WfDialogSpaceVariant) => {
    if (space === WF_DIALOG_SPACE_VARIANTS.NONE) {
        return { p: 0 };
    }
    return space === WF_DIALOG_SPACE_VARIANTS.MOBILE_SPACIOUS_TOP
        ? {
              pt: { xs: 9, sm: 5 },
              pb: { xs: 3, sm: 5 },
              px: { xs: 3, sm: 5 },
          }
        : {
              p: { xs: 3, sm: 5 },
          };
};

export const WF_DIALOG_CLOSE_REASONS = {
    BACKDROP_CLICK: "backdropClick",
    ESCAPE_KEY_DOWN: "escapeKeyDown",
} as const;

export type WfDialogCloseReason = (typeof WF_DIALOG_CLOSE_REASONS)[keyof typeof WF_DIALOG_CLOSE_REASONS];

interface IWfDialogProps {
    isOpen: boolean;
    onClose: () => void;
    children: ReactNode;
    ariaLabelledBy?: string;
    ariaDescribedBy?: string;
    showCloseButton?: boolean;
    space?: WfDialogSpaceVariant;
    sx?: SxProps<Theme>;
    container?: () => HTMLElement | null;
    alignTop?: boolean;
    ignoredCloseReasons?: ReadonlyArray<WfDialogCloseReason>;
}

export const WfDialog = ({
    isOpen,
    onClose,
    children,
    ariaLabelledBy,
    ariaDescribedBy,
    showCloseButton = true,
    space = WF_DIALOG_SPACE_VARIANTS.DEFAULT,
    sx,
    container,
    alignTop = false,
    ignoredCloseReasons = [],
}: IWfDialogProps) => {
    const { t } = useTranslation("common");

    const handleClose = (_event?: object, reason?: WfDialogCloseReason) => {
        if (reason && ignoredCloseReasons.includes(reason)) {
            return;
        }

        blurActiveElement();
        onClose();
    };

    return (
        <Dialog
            open={isOpen}
            onClose={handleClose}
            fullWidth
            container={container}
            closeAfterTransition={false}
            // Prevents MUI's focus trap from moving focus back into the dialog
            // during the exit transition, which would leave a focused
            // descendant inside the `aria-hidden` modal root and trigger
            // Chrome's "Blocked aria-hidden ... descendant retained focus"
            // warning. Combined with `handleClose` blurring the active element,
            // focus lands on the document body while the dialog animates out.
            // See https://github.com/mui/material-ui/issues/43106
            disableRestoreFocus
            slots={{ transition: Grow }}
            slotProps={{
                transition: {
                    timeout: 250,
                    style: { transformOrigin: "center" },
                },
            }}
            role="dialog"
            aria-modal="true"
            aria-labelledby={ariaLabelledBy}
            aria-describedby={ariaDescribedBy}
            sx={{
                "& .MuiDialog-container": {
                    alignItems: alignTop ? "flex-start" : "center",
                },
                "& .MuiDialog-paper": {
                    width: { xs: "100%", sm: "750px", md: "750px", lg: "708px" },
                    borderRadius: { xs: 0, sm: 1 },
                    m: 0,
                    ...(alignTop && { mt: { xs: 0, sm: "60px" } }),
                    minHeight: { xs: "100%", sm: "auto" },
                    maxHeight: { xs: "100%", sm: "90%" },
                    position: "relative",
                    display: "flex",
                    flexDirection: "column",
                    ...getSpacing(space),
                    ...(sx as object),
                },
            }}
        >
            {showCloseButton && (
                <IconButton
                    variant="ghost"
                    sx={{
                        position: "absolute",
                        top: { xs: "24px", sm: "40px" },
                        right: { xs: "24px", sm: "40px" },
                        borderRadius: "6px",
                        zIndex: 1,
                    }}
                    size="small"
                    aria-label={t("close")}
                    onClick={handleClose}
                >
                    <IconClose sx={{ width: "24px", height: "24px" }} />
                </IconButton>
            )}
            {children}
        </Dialog>
    );
};
